/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1904017435 — SmilePage */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { ContentBox, PrimaryButton, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { debug } from '../../../../shared/lib/general';
import { SmileBadColor, SmileGoodColor } from '../../components/CheckboxSvg';
import { SmileTitle } from '../../components/SmileTitle';
import styles from '../../components/FeedbacksStyles.module.css';
import {
    DISABLE_OR_ENABLE_BUTTON,
    FEEDBACKS_CUSTOMER_STATUS,
    FEEDBACKS_ESTIMATE,
    FEEDBACK_ROUTES,
} from '../../feedback.constants';
import { UpdateFeedback } from '../../feedback.types';
import { useCreateTelemetryEvent } from '../../hooks/useCreateTelemetryEvent';
import { useFeedbackStore } from '../../hooks/useFeedbackStore';
import { useUpdateFeedbacks } from '../../hooks/useUpdateFeedbacks';
import { useUpdateTelemetryEvent } from '../../hooks/useUpdateTelemetryEvent';

export const SmilePage = () => {
    const feedback = useFeedbackStore((s) => s.feedback);
    const updatedFeedback = useFeedbackStore((s) => s.updateFeedback);
    const setUpdateFeedback = useFeedbackStore((s) => s.setUpdateFeedback);
    const setPageNumber = useFeedbackStore((s) => s.setPageNumber);
    const setNumberVisibility = useFeedbackStore((s) => s.setPageNumberVisible);
    const telemetryCheckID = useFeedbackStore((s) => s.telemetryEvent);
    const setTelemetryCheckID = useFeedbackStore((s) => s.setTelemetryEvent);
    const vm = useAppStore((s) => s.vm);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const updateFeedbacksMutation = useUpdateFeedbacks();
    const createTelemetryMutation = useCreateTelemetryEvent();
    const updateTelemetryMutation = useUpdateTelemetryEvent();
    const isSending =
        updateFeedbacksMutation.isPending ||
        createTelemetryMutation.isPending ||
        updateTelemetryMutation.isPending;

    useEffect(() => {
        setPageNumber('1/3');
        setNumberVisibility(true);
    }, []);

    const handleSmileBadChange = async (event: React.MouseEvent<HTMLButtonElement>) => {
        // Guard against clicks before /api/get_feedback_data has resolved —
        // updating with feedback.id === 0 would mutate the wrong row.
        if (!feedback.id) {
            debug('Feedback not yet loaded — ignoring smile click', 'warn');
            return;
        }
        if (isSending) return;

        const { id } = event.currentTarget;

        const isGoodFeedback = id === 'Good';
        const nextRoute = isGoodFeedback
            ? FEEDBACK_ROUTES.wishes
            : FEEDBACK_ROUTES.checkbox;
        const estimate = isGoodFeedback
            ? FEEDBACKS_ESTIMATE.POSITIVE_SMILE
            : FEEDBACKS_ESTIMATE.NEGATIVE_SMILE;

        const newfeedback: UpdateFeedback = {
            ...updatedFeedback,
            chosen_options: null,
            custom_content: null,
            customer_name: null,
            customer_phone_number: null,
            estimate,
            customer_status: FEEDBACKS_CUSTOMER_STATUS.AFTER_SMILE_PAGE,
            id: feedback.id,
        };

        setUpdateFeedback(newfeedback);

        const telemetryPromise = telemetryCheckID.id != null
            ? updateTelemetryMutation.mutateAsync({
                  id: feedback.id,
                  est: estimate,
                  event: telemetryCheckID.id,
              })
            : createTelemetryMutation
                  .mutateAsync({ id: feedback.id, est: estimate })
                  .then((data) => setTelemetryCheckID(data));

        try {
            await Promise.all([
                updateFeedbacksMutation.mutateAsync({ id: feedback.id, body: newfeedback }),
                telemetryPromise,
            ]);
        } catch (e) {
            debug(`Smile submit failed: ${String(e)}`, 'error');
        }

        navigate(generatePath(nextRoute, { id: vm.qr }));
    };

    const onCancelClick = () => {
        setNumberVisibility(false);
        navigate(generatePath(FEEDBACK_ROUTES.complete, { id: vm.qr }), {
            replace: true,
        });
    };

    return (
        <ScreenWrapper>
            <ScreenTitle>{t('Rate your visit')}</ScreenTitle>
            <ContentBox>
                <div className={styles.checkboxBox}>
                    <div>
                        <button
                            type="button"
                            className={styles.customCheckbox}
                            id="Bad"
                            disabled={isSending}
                            onClick={handleSmileBadChange}
                        >
                            <SmileBadColor />
                        </button>
                        <SmileTitle text={t('Bad')} />
                    </div>
                    <div>
                        <button
                            type="button"
                            className={styles.customCheckbox}
                            id="Good"
                            disabled={isSending}
                            onClick={handleSmileBadChange}
                        >
                            <SmileGoodColor />
                        </button>
                        <SmileTitle text={t('Great')} />
                    </div>
                </div>

                <div className={styles.feedbacksButtonBoxCenter}>
                    <PrimaryButton
                        className={styles.feedbacksButton}
                        onClick={onCancelClick}
                        style={DISABLE_OR_ENABLE_BUTTON[1]}
                    >
                        {t('Cancel')}
                    </PrimaryButton>
                </div>
            </ContentBox>
        </ScreenWrapper>
    );
};
